<template>
  <Drawer
    v-model:visible="visible"
    position="right"
    header="Pull Documents"
    :style="{ width: '36rem' }"
  >
    <!-- Subtitle -->
    <p class="text-navy-400 mb-5 text-xs">From {{ agentName }}</p>

    <!-- Loading state -->
    <div v-if="isFetching" class="flex items-center justify-center gap-3 py-12">
      <i class="pi pi-spin pi-spinner text-navy-400 text-lg" />
      <span class="text-navy-500 text-sm">
        Fetching documents from {{ agentName }}...
      </span>
    </div>

    <!-- Error state -->
    <div v-else-if="fetchError" class="space-y-4">
      <div class="rounded-lg border border-red-200 bg-red-50 p-4">
        <p class="text-sm text-red-600">{{ fetchError }}</p>
      </div>
      <AppButton variant="secondary" @click="fetchDocuments"> Retry </AppButton>
    </div>

    <!-- Document selection -->
    <template v-else-if="fetchedDocuments">
      <!-- Already-imported banner -->
      <div
        v-if="alreadyImportedIds.size > 0"
        class="mb-4 flex items-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2"
      >
        <i class="pi pi-check-circle text-sm text-green-500" />
        <span class="text-navy-500 text-xs">
          {{ alreadyImportedIds.size }}
          {{ alreadyImportedIds.size === 1 ? "document" : "documents" }}
          already imported
        </span>
      </div>

      <!-- Name changes banner -->
      <div
        v-if="nameChanges.length > 0"
        class="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
      >
        <p class="text-navy-700 text-sm font-medium">
          {{ nameChanges.length }}
          document {{ nameChanges.length === 1 ? "name" : "names" }} changed in
          {{ agentName }}
        </p>
        <div class="mt-2 space-y-1.5">
          <label
            v-for="change in nameChanges"
            :key="change.externalId"
            class="flex cursor-pointer items-center gap-2 text-xs"
          >
            <input
              type="checkbox"
              :checked="acceptedDocNameUpdates.has(change.externalId)"
              class="h-3.5 w-3.5 shrink-0 accent-amber-500"
              @change="toggleDocNameUpdate(change.externalId)"
            />
            <span class="text-navy-600">
              <span class="text-navy-400">"{{ change.oldName }}"</span>
              <span class="text-navy-400 mx-0.5">&rarr;</span>
              <span class="font-medium">"{{ change.newName }}"</span>
            </span>
          </label>
        </div>
      </div>

      <div v-if="fetchedDocuments.length > 0">
        <DocumentTree
          :documents="fetchedDocuments"
          :selected-ids="allSelectedIds"
          @toggle-document="handleToggleDocument"
          @toggle-folder="handleToggleFolder"
          @select-all="handleSelectAll"
          @deselect-all="handleDeselectAll"
        />
      </div>

      <div
        v-else
        class="flex flex-col items-center justify-center py-12 text-center"
      >
        <i class="pi pi-inbox text-navy-300 mb-3 text-3xl" />
        <p class="text-navy-500 text-sm">
          No documents found for this file number.
        </p>
      </div>

      <!-- Refresh hint -->
      <div
        class="mt-4 flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3"
      >
        <i class="pi pi-info-circle text-blue-500" />
        <div class="flex-1">
          <p class="text-navy-700 text-sm font-medium">
            Not seeing a document?
          </p>
          <p class="text-navy-500 mt-0.5 text-xs">
            Documents may have been added in {{ agentName }} since this list was
            last loaded.
          </p>
        </div>
        <button
          class="cursor-pointer rounded-md border border-blue-300 bg-white px-3 py-1.5 text-xs font-semibold text-blue-600 transition-colors hover:bg-blue-50"
          @click="fetchDocuments"
        >
          Refresh
        </button>
      </div>
    </template>

    <template #footer>
      <div class="flex items-center justify-between">
        <button
          class="text-navy-500 hover:text-navy-700 cursor-pointer text-sm transition-colors"
          @click="visible = false"
        >
          Cancel
        </button>
        <AppButton
          :loading="isSaving"
          :disabled="
            newSelectedIds.length === 0 && activeDocNameUpdateCount === 0
          "
          @click="importSelectedDocuments"
        >
          {{ importButtonLabel }}
        </AppButton>
      </div>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import Drawer from "primevue/drawer"
import { useToast } from "primevue/usetoast"
import { computed, onUnmounted, ref, watch } from "vue"

import type {
  FetchedDocument,
  FetchedDocumentsResponse,
  FetchedOrderAttachment,
  OrderType,
} from "../../pages/Order/types"
import DISPATCH_FETCH_DOCUMENTS from "../../queries/agent_registration/dispatchFetchDocuments.gql"
import POLL_WORK_ITEM from "../../queries/agent_work_item/poll.gql"
import { apolloClient } from "../../queries/client"
import IMPORT_DOCUMENTS from "../../queries/order/importDocuments.gql"
import AppButton from "../ui/AppButton.vue"
import DocumentTree from "./DocumentTree.vue"

const props = defineProps<{
  order: OrderType
}>()

const visible = defineModel<boolean>("visible", { required: true })
const emit = defineEmits<{ success: [] }>()

const toast = useToast()
const isFetching = ref(false)
const isSaving = ref(false)
const fetchError = ref<string | undefined>()
const fetchedDocuments = ref<FetchedDocument[] | null>(null)
const selectedNewIds = ref<Set<string>>(new Set())
const attachmentTree = ref<unknown>(null)
let pollInterval: ReturnType<typeof setInterval> | null = null

const documentNodes = computed(() =>
  (props.order.documents.nodes ?? []).filter(
    (d): d is NonNullable<typeof d> => d != null,
  ),
)

const agentName = computed(
  () => props.order.importAgentRegistration?.name ?? "source system",
)

const alreadyImportedIds = computed(() => {
  const ids = new Set<string>()
  for (const doc of documentNodes.value) {
    if (doc.externalId) ids.add(doc.externalId)
  }
  return ids
})

// ─── Name change detection ───────────────────────────────────────

interface DocNameChange {
  externalId: string
  oldName: string
  newName: string
}

const nameChanges = computed<DocNameChange[]>(() => {
  if (!fetchedDocuments.value) return []
  const changes: DocNameChange[] = []
  for (const doc of fetchedDocuments.value) {
    const existing = documentNodes.value.find(
      (d) => d.externalId === doc.document_id,
    )
    if (existing && existing.name !== doc.file_name) {
      changes.push({
        externalId: doc.document_id,
        oldName: existing.name,
        newName: doc.file_name,
      })
    }
  }
  return changes
})

const acceptedDocNameUpdates = ref<Set<string>>(new Set())

const toggleDocNameUpdate = (externalId: string) => {
  const next = new Set(acceptedDocNameUpdates.value)
  if (next.has(externalId)) {
    next.delete(externalId)
  } else {
    next.add(externalId)
  }
  acceptedDocNameUpdates.value = next
}

const activeDocNameUpdateCount = computed(
  () => acceptedDocNameUpdates.value.size,
)

const importButtonLabel = computed(() => {
  const parts: string[] = []
  if (newSelectedIds.value.length > 0) {
    parts.push(
      `Import ${newSelectedIds.value.length} ${newSelectedIds.value.length === 1 ? "Document" : "Documents"}`,
    )
  }
  if (activeDocNameUpdateCount.value > 0) {
    parts.push(
      `Update ${activeDocNameUpdateCount.value} ${activeDocNameUpdateCount.value === 1 ? "name" : "names"}`,
    )
  }
  return parts.join(", ") || "No changes"
})

// Initialize accepted name updates when name changes are detected
watch(nameChanges, (changes) => {
  const ids = new Set<string>()
  for (const c of changes) {
    ids.add(c.externalId)
  }
  acceptedDocNameUpdates.value = ids
})

// All selected IDs = already-imported (always checked) + newly selected
const allSelectedIds = computed(() => [
  ...alreadyImportedIds.value,
  ...selectedNewIds.value,
])

// Only the new selections (not already imported)
const newSelectedIds = computed(() => [...selectedNewIds.value])

// ─── Cache resolution ───────────────────────────────────────────

/**
 * sourceAttachmentTree may be stored in two formats:
 * 1. FetchedOrderAttachment[] (PascalCase) — from initial import wizard
 * 2. FetchedDocumentsResponse { documents: FetchedDocument[] } — from previous pull
 * Convert either to FetchedDocument[].
 */
const parseCachedDocuments = (tree: unknown): FetchedDocument[] | null => {
  if (!tree) return null

  // Format 2: FetchedDocumentsResponse with documents array
  if (
    typeof tree === "object" &&
    tree !== null &&
    "documents" in tree &&
    Array.isArray((tree as FetchedDocumentsResponse).documents)
  ) {
    return (tree as FetchedDocumentsResponse).documents
  }

  // Format 1: FetchedOrderAttachment[] (PascalCase keys)
  if (Array.isArray(tree) && tree.length > 0 && "SourceId" in tree[0]) {
    return (tree as FetchedOrderAttachment[]).map((a) => ({
      document_id: a.SourceId,
      file_name: a.FileName,
      folder_path: a.FolderPath,
      content_type: a.ContentType,
      size_bytes: a.SizeBytes,
    }))
  }

  return null
}

const preselectNewDocuments = () => {
  selectedNewIds.value = new Set<string>()
}

// ─── Fetch + Poll ───────────────────────────────────────────────

const cleanup = () => {
  if (pollInterval) {
    clearInterval(pollInterval)
    pollInterval = null
  }
}

onUnmounted(cleanup)

watch(visible, (isOpen) => {
  if (isOpen) {
    selectedNewIds.value = new Set()
    fetchError.value = undefined

    // Try cached data first
    const cached = parseCachedDocuments(props.order.sourceAttachmentTree)
    if (cached) {
      fetchedDocuments.value = cached
      attachmentTree.value = props.order.sourceAttachmentTree
      preselectNewDocuments()
    } else {
      fetchedDocuments.value = null
      attachmentTree.value = null
      fetchDocuments()
    }
  } else {
    cleanup()
  }
})

const fetchDocuments = async () => {
  cleanup()
  isFetching.value = true
  fetchError.value = undefined
  fetchedDocuments.value = null

  try {
    const result = await apolloClient.mutate({
      mutation: DISPATCH_FETCH_DOCUMENTS,
      variables: {
        agentRegistrationId: props.order.importAgentRegistration!.id,
        fileNumber: props.order.externalFileNumber,
      },
    })

    if (result?.data?.dispatchFetchDocuments?.errors?.length) {
      fetchError.value = result.data.dispatchFetchDocuments.errors.join(", ")
      isFetching.value = false
      return
    }

    const workItemId = result.data.dispatchFetchDocuments.workItem.id
    pollForResult(workItemId)
  } catch (error) {
    console.error("Dispatch error:", error)
    fetchError.value = "Failed to dispatch document fetch request"
    isFetching.value = false
  }
}

const pollForResult = (workItemId: string) => {
  cleanup()
  pollInterval = setInterval(() => {
    void pollOnce(workItemId)
  }, 2000)
}

const pollOnce = async (workItemId: string) => {
  try {
    const result = await apolloClient.query({
      query: POLL_WORK_ITEM,
      variables: { id: workItemId },
      fetchPolicy: "network-only",
    })

    const item = result.data.agentWorkItem
    if (!item) return

    if (item.status === "completed") {
      cleanup()
      isFetching.value = false
      const response = item.result as FetchedDocumentsResponse
      fetchedDocuments.value = response.documents
      attachmentTree.value = response

      preselectNewDocuments()
    } else if (item.status === "failed") {
      cleanup()
      isFetching.value = false
      fetchError.value = item.errorMessage || "Failed to fetch documents"
    }
  } catch (error) {
    console.error("Poll error:", error)
    cleanup()
    isFetching.value = false
    fetchError.value = "Lost connection while fetching documents"
  }
}

// ─── Selection handlers ─────────────────────────────────────────

const handleToggleDocument = (documentId: string) => {
  if (alreadyImportedIds.value.has(documentId)) return

  const next = new Set(selectedNewIds.value)
  if (next.has(documentId)) {
    next.delete(documentId)
  } else {
    next.add(documentId)
  }
  selectedNewIds.value = next
}

const handleToggleFolder = (folderPath: string) => {
  if (!fetchedDocuments.value) return

  const docsInFolder = fetchedDocuments.value.filter((d) => {
    const normalized = d.folder_path.replace(/\\/g, "/").replace(/^\/+/, "")
    const parts = normalized.split("/").filter(Boolean)
    const folderOnly = parts.slice(0, -1).join("/")
    return folderOnly === folderPath || folderOnly.startsWith(folderPath + "/")
  })

  const newDocsInFolder = docsInFolder.filter(
    (d) => !alreadyImportedIds.value.has(d.document_id),
  )
  if (newDocsInFolder.length === 0) return

  const allSelected = newDocsInFolder.every((d) =>
    selectedNewIds.value.has(d.document_id),
  )

  const next = new Set(selectedNewIds.value)
  for (const doc of newDocsInFolder) {
    if (allSelected) {
      next.delete(doc.document_id)
    } else {
      next.add(doc.document_id)
    }
  }
  selectedNewIds.value = next
}

const handleSelectAll = () => {
  if (!fetchedDocuments.value) return
  const next = new Set<string>()
  for (const doc of fetchedDocuments.value) {
    if (!alreadyImportedIds.value.has(doc.document_id)) {
      next.add(doc.document_id)
    }
  }
  selectedNewIds.value = next
}

const handleDeselectAll = () => {
  selectedNewIds.value = new Set()
}

// ─── Import ─────────────────────────────────────────────────────

const importSelectedDocuments = async () => {
  if (!fetchedDocuments.value) return

  const docsToImport = fetchedDocuments.value.filter((d) =>
    selectedNewIds.value.has(d.document_id),
  )

  const pendingNameUpdates = nameChanges.value
    .filter((c) => acceptedDocNameUpdates.value.has(c.externalId))
    .map((c) => ({ externalId: c.externalId, newName: c.newName }))

  if (docsToImport.length === 0 && pendingNameUpdates.length === 0) return

  isSaving.value = true

  try {
    const variables: Record<string, unknown> = {
      orderId: props.order.gid,
      documents: docsToImport.map((d) => ({
        documentId: d.document_id,
        fileName: d.file_name,
        folderPath: d.folder_path,
        contentType: d.content_type,
        sizeBytes: d.size_bytes,
      })),
      sourceAttachmentTree: attachmentTree.value,
    }

    if (pendingNameUpdates.length > 0) {
      variables.nameUpdates = pendingNameUpdates
    }

    const result = await apolloClient.mutate({
      mutation: IMPORT_DOCUMENTS,
      variables,
    })

    if (result?.data?.importDocuments?.errors?.length) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: result.data.importDocuments.errors.join(", "),
        life: 5000,
      })
      return
    }

    const details: string[] = []
    if (docsToImport.length > 0) {
      details.push(
        `Importing ${docsToImport.length} ${docsToImport.length === 1 ? "document" : "documents"}`,
      )
    }
    if (pendingNameUpdates.length > 0) {
      details.push(
        `Updated ${pendingNameUpdates.length} ${pendingNameUpdates.length === 1 ? "name" : "names"}`,
      )
    }

    toast.add({
      severity: "success",
      summary: "Success",
      detail: details.join(", "),
      life: 4000,
    })

    visible.value = false
    emit("success")
  } catch (error) {
    console.error("Mutation error:", error)
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to start document import",
      life: 5000,
    })
  } finally {
    isSaving.value = false
  }
}
</script>
